// 管理员认证工具 - 使用localStorage存储，支持页面刷新
class AdminAuthManager {
  constructor() {
    this.ADMIN_PREFIX = 'admin_';
    this.SESSION_KEY = 'admin_session';
    this.TOKEN_KEY = 'admin_token';
    this.USER_INFO_KEY = 'admin_user_info';
    this.PERMISSIONS_KEY = 'admin_permissions';
  }

  // 设置管理员token（使用localStorage持久化存储）
  setAdminToken(token) {
    // 使用localStorage，支持页面刷新
    localStorage.setItem(this.TOKEN_KEY, token);
    // 设置token过期时间（24小时）
    const expiryTime = new Date().getTime() + 24 * 60 * 60 * 1000;
    localStorage.setItem(`${this.TOKEN_KEY}_expiry`, expiryTime.toString());
  }

  // 设置管理员JWT token（新模块使用）
  setAdminJwtToken(jwtToken) {
    localStorage.setItem(`${this.TOKEN_KEY}_jwt`, jwtToken);
    // JWT token的过期时间由后端控制，这里设置一个较长的过期时间作为备用
    const expiryTime = new Date().getTime() + 24 * 60 * 60 * 1000;
    localStorage.setItem(`${this.TOKEN_KEY}_jwt_expiry`, expiryTime.toString());
  }

  // 获取管理员token
  getAdminToken() {
    const token = localStorage.getItem(this.TOKEN_KEY);
    if (token) {
      const expiry = localStorage.getItem(`${this.TOKEN_KEY}_expiry`);
      if (expiry) {
        const currentTime = new Date().getTime();
        if (currentTime > parseInt(expiry)) {
          // token已过期，清除所有管理员数据
          this.clearAdminData();
          // 清除Vuex store中的管理员相关数据
          this.clearVuexStoreData();
          return null;
        }
      }
      return token;
    }
    return null;
  }

  // 获取管理员JWT token（新模块使用）
  getAdminJwtToken() {
    const jwtToken = localStorage.getItem(`${this.TOKEN_KEY}_jwt`);
    if (jwtToken) {
      const expiry = localStorage.getItem(`${this.TOKEN_KEY}_jwt_expiry`);
      if (expiry) {
        const currentTime = new Date().getTime();
        if (currentTime > parseInt(expiry)) {
          // JWT token已过期，清除所有管理员数据
          this.clearAdminData();
          // 清除Vuex store中的管理员相关数据
          this.clearVuexStoreData();
          return null;
        }
      }
      return jwtToken;
    }
    return null;
  }

  // 获取有效的管理员token（优先使用JWT token）
  getValidAdminToken() {
    // 优先使用JWT token（新模块）
    const jwtToken = this.getAdminJwtToken();
    if (jwtToken) {
      return jwtToken;
    }
    
    // 回退到普通token（旧模块）
    return this.getAdminToken();
  }

  // 设置管理员用户信息（使用localStorage持久化存储）
  setAdminUserInfo(userInfo) {
    localStorage.setItem(this.USER_INFO_KEY, JSON.stringify(userInfo));
  }

  // 获取管理员用户信息
  getAdminUserInfo() {
    // 先检查token是否存在，避免token已清除但用户信息仍存在的情况
    const token = this.getAdminToken();
    if (!token) {
      // token不存在，清除用户信息并返回null
      localStorage.removeItem(this.USER_INFO_KEY);
      return null;
    }
    
    const userInfo = localStorage.getItem(this.USER_INFO_KEY);
    return userInfo ? JSON.parse(userInfo) : null;
  }

  // 设置管理员权限信息（使用localStorage持久化存储）
  setAdminPermissions(permissions) {
    localStorage.setItem(this.PERMISSIONS_KEY, JSON.stringify(permissions));
  }

  // 获取管理员权限信息
  getAdminPermissions() {
    const permissions = localStorage.getItem(this.PERMISSIONS_KEY);
    return permissions ? JSON.parse(permissions) : {
      canFileManage: false,
      canUserManage: false,
      canSystemManage: false,
      role: '',
      permissionLevel: 0
    };
  }

  // 检查管理员是否已登录
  isAdminLoggedIn() {
    const token = this.getAdminToken();
    const userInfo = this.getAdminUserInfo();
    return !!(token && userInfo);
  }

  // 检查特定权限
  hasPermission(permission) {
    if (!this.isAdminLoggedIn()) return false;
    const permissions = this.getAdminPermissions();
    return permissions[permission] || false;
  }

  // 检查权限级别
  hasPermissionLevel(requiredLevel) {
    if (!this.isAdminLoggedIn()) return false;
    const permissions = this.getAdminPermissions();
    return permissions.permissionLevel >= requiredLevel;
  }

  // 清除Vuex store中的管理员相关数据
  clearVuexStoreData() {
    try {
      // 动态导入store，避免循环依赖
      const store = require('../store/index').default;
      if (store && store.dispatch) {
        store.dispatch("resetUserState");
      }
    } catch (error) {
      // 如果无法导入store，静默处理
      console.warn('无法清除Vuex store数据:', error);
    }
  }

  // 清除所有管理员数据
  clearAdminData() {
    localStorage.removeItem(this.TOKEN_KEY);
    localStorage.removeItem(`${this.TOKEN_KEY}_expiry`);
    localStorage.removeItem(`${this.TOKEN_KEY}_jwt`);
    localStorage.removeItem(`${this.TOKEN_KEY}_jwt_expiry`);
    localStorage.removeItem(this.USER_INFO_KEY);
    localStorage.removeItem(this.PERMISSIONS_KEY);
    localStorage.removeItem(this.SESSION_KEY);
    // 同时清除Vuex store中的数据
    this.clearVuexStoreData();
  }

  // 管理员登出
  async adminLogout() {
    try {
      // 先调用后端登出接口
      const api = require('@/api/api').default;
      await api.adminLogout();
    } catch (error) {
      // 后端登出失败，继续本地登出
    } finally {
      // 无论后端登出是否成功，都要清除本地数据
      this.clearAdminData();
    }
    return Promise.resolve();
  }

  // 创建管理员会话
  createAdminSession(token, userInfo, permissions, jwtToken = null) {
    this.setAdminToken(token);
    if (jwtToken) {
      this.setAdminJwtToken(jwtToken);
    }
    this.setAdminUserInfo(userInfo);
    this.setAdminPermissions(permissions);
    // 设置会话标识
    localStorage.setItem(this.SESSION_KEY, 'active');
  }

  // 获取完整的认证状态
  getAuthState() {
    return {
      isLoggedIn: this.isAdminLoggedIn(),
      token: this.getAdminToken(),
      jwtToken: this.getAdminJwtToken(),
      validToken: this.getValidAdminToken(),
      userInfo: this.getAdminUserInfo(),
      permissions: this.getAdminPermissions()
    };
  }
}

// 创建单例实例
const adminAuth = new AdminAuthManager();

// 导出便捷方法
export const setAdminToken = (token) => adminAuth.setAdminToken(token);
export const setAdminJwtToken = (jwtToken) => adminAuth.setAdminJwtToken(jwtToken);
export const getAdminToken = () => adminAuth.getAdminToken();
export const getAdminJwtToken = () => adminAuth.getAdminJwtToken();
export const getValidAdminToken = () => adminAuth.getValidAdminToken();
export const setAdminUserInfo = (userInfo) => adminAuth.setAdminUserInfo(userInfo);
export const getAdminUserInfo = () => adminAuth.getAdminUserInfo();
export const setAdminPermissions = (permissions) => adminAuth.setAdminPermissions(permissions);
export const getAdminPermissions = () => adminAuth.getAdminPermissions();
export const isAdminLoggedIn = () => adminAuth.isAdminLoggedIn();
export const hasAdminPermission = (permission) => adminAuth.hasPermission(permission);
export const hasAdminPermissionLevel = (level) => adminAuth.hasPermissionLevel(level);
export const clearAdminData = () => adminAuth.clearAdminData();
export const adminLogout = () => adminAuth.adminLogout();
export const createAdminSession = (token, userInfo, permissions, jwtToken) => adminAuth.createAdminSession(token, userInfo, permissions, jwtToken);
export const getAdminAuthState = () => adminAuth.getAuthState();

// 权限判断函数 - 权限向上兼容版本
export const canAccessFileManagement = (userInfo) => {
  if (!userInfo) return false;
  // 文件管理权限：有文件管理、系统管理或用户管理权限都可以
  return userInfo.canFileManage || userInfo.canUserManage || userInfo.canSystemManage;
};

export const canAccessCategoryManagement = (userInfo) => {
  if (!userInfo) return false;
  // 分类管理权限：需要系统管理或用户管理权限
  return userInfo.canSystemManage || userInfo.canUserManage;
};

export const canAccessEcardManagement = (userInfo) => {
  if (!userInfo) return false;
  // 卡密管理权限：只有用户管理权限才能管理
  return userInfo.canUserManage;
};

export const getAccessibleManagementFeatures = (userInfo) => {
  if (!userInfo) return [];
  
  const features = [];
  
  if (canAccessFileManagement(userInfo)) {
    features.push({
      name: 'FileManagement',
      title: '文件管理',
      icon: 'el-icon-folder',
      description: '管理文件上传、编辑、删除等操作'
    });
  }
  
  if (canAccessCategoryManagement(userInfo)) {
    features.push({
      name: 'CategoryManagement',
      title: '分类管理',
      icon: 'el-icon-menu',
      description: '管理大分类和子分类'
    });
  }
  
  if (canAccessEcardManagement(userInfo)) {
    features.push({
      name: 'EcardManagement',
      title: '卡密管理',
      icon: 'el-icon-tickets',
      description: '管理新文科模块电子卡'
    });
  }
  
  return features;
};

export default adminAuth;
